"use client";
import { useState } from 'react';
import { toast } from 'sonner';
import { trackConversion } from '@/utils/conversionTracking';

// PERF: no static supabase import. FooterNewsletter mounts on EVERY marketing
// page, and a static chain here bundled supabase-js (~64KB gz) into every
// page's first load. The modules are loaded on-demand inside subscribe() —
// i.e. only in the moment a visitor actually submits the form.
const loadDeps = async () => {
  const [{ supabase }, { invokeEdgeFunction }] = await Promise.all([
    import('@/lib/supabaseClient'),
    import('@/lib/demoBookingApi'),
  ]);
  return { supabase, invokeEdgeFunction };
};

interface UseNewsletterSubscribeOptions {
  source?: string;
  onSuccess?: () => void;
}

export function useNewsletterSubscribe({ source = 'website', onSuccess }: UseNewsletterSubscribeOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);

  const subscribe = async (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      toast.error('Please enter your email address to subscribe.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error('Please enter a valid email address.');
      return false;
    }

    setIsLoading(true);

    try {
      const { supabase, invokeEdgeFunction } = await loadDeps();

      // Log every signup event (audit trail) — fire-and-forget.
      supabase
        .from('newsletter_signup_events')
        .insert({ email: trimmedEmail, source } as never)
        .then(({ error }: { error: { message: string } | null }) => {
          if (error) console.warn('Failed to log signup event:', error.message);
        });

      // Insert into the deduplicated subscribers list.
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({ email: trimmedEmail, source } as never);

      if (error) {
        if ((error as { code?: string }).code === '23505') {
          // Duplicate — treat as friendly, not an error.
          toast.info("You're already subscribed — this email is already on our list.");
          onSuccess?.();
          return true;
        }
        throw error;
      }

      // Welcome email — fire-and-forget, non-blocking.
      invokeEdgeFunction('send-welcome-email', {
        userEmail: trimmedEmail,
      }).catch((err) => {
        console.error('[welcome-email] send failed for', trimmedEmail, '-', err?.message || err);
      });

      // CRM lead — fire-and-forget, non-blocking. create-lead upserts by email,
      // so this also shows up on /platform/leads (previously nothing did).
      invokeEdgeFunction('create-lead', {
        email: trimmedEmail,
        source,
      }).catch((err) => {
        console.error('[create-lead] failed for', trimmedEmail, '-', err?.message || err);
      });

      // Funnel conversion. Deliberately NOT fired on the duplicate branch above:
      // a re-submit by someone already subscribed is not a second conversion, and
      // counting it would inflate the rate the dashboard reports.
      trackConversion('email_signup', 'Newsletter Subscribe', { source });

      toast.success("You'll receive our latest HR insights and updates.");
      onSuccess?.();
      return true;
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Something went wrong. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { subscribe, isLoading };
}
